import { LoadingFrame, useLogger, useToasts, type ResponseResolver } from '@synqo/frontend-component-library';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FC } from 'react';
import classes from './TemplatePreview.module.css';
import { useDocumentsRepository } from '@Hooks/useRepository';
import { FilesRepository } from '@Repositories/Documents';

const blobResolver: ResponseResolver<Blob> = async (res) => {
    if (!res.ok) {
        const msg = await res.text();
        throw new Error(`Loading png preview returned ${res.status}: ${msg}`);
    }
    return res.blob();
};

export interface DocumentPngPreviewProps {
    label: string;
    previewFileId: string;
    onError?: () => void;
}

export const DocumentPngPreview: FC<DocumentPngPreviewProps> = ({ label, previewFileId, onError }) => {
    const { warn } = useLogger();
    const { pushToast } = useToasts();
    const files = useDocumentsRepository(FilesRepository);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const {
        data: blob,
        isFetching,
        isError,
        error,
    } = useQuery({
        queryKey: ['documents', 'files', previewFileId, 'preview'],
        retry: 1,
        queryFn: () => files.downloadFile(previewFileId, blobResolver),
    });

    useEffect(() => {
        if (isError) {
            pushToast({ message: 'Die Vorschau konnte nicht geladen werden.', variant: 'error' });
            if (error instanceof Error) {
                warn(error.message);
            }
            onError?.();
        }
    }, [isError, error, pushToast, warn, onError]);

    useEffect(() => {
        if (!blob) {
            return;
        }

        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);

        return () => {
            URL.revokeObjectURL(url);
            setPreviewUrl(null);
        };
    }, [blob]);

    if (isFetching) {
        return <LoadingFrame />;
    }

    if (!previewUrl) {
        return null;
    }

    return <img className={classes.template} src={previewUrl} alt={`Preview for '${label}'`} />;
};
